/**
 * How an OTIF or PPM grade band's range is listed and edited (CR-8677).
 *
 * Bands are stored half-open, [min, max): a band's max is the next band's min,
 * and a score on the line belongs to the band above it (50 is D, not F).
 * Listed as stored, two neighbours printed the same number ("0.00 - 50.00"
 * under "50.00 - 70.00") and 50 read as if it sat in both. A band with a band
 * above it now shows its max one step below that band's min (F 0.00 - 49.99),
 * and a max typed into the editor is stored one step up again. The band at the
 * top of the scale shows its stored max (100.00).
 */
import type { OTIFGradeThreshold, PPMGradeThreshold } from './api';

/** The step a band limit is stored to (two decimal places). */
export const BAND_STEP = 0.01;

/** A band's limits as numbers. `id` is absent for a band not yet saved. */
export interface BandLimits {
  id?: number;
  min: number;
  max: number;
}

const toStep = (value: number): number => Math.round(value * 100) / 100;

/** An OTIF band's limits. */
export const otifLimits = (b: OTIFGradeThreshold): BandLimits => ({
  id: b.id, min: parseFloat(b.min_otif_percent), max: parseFloat(b.max_otif_percent),
});

/** A PPM band's limits. */
export const ppmLimits = (b: PPMGradeThreshold): BandLimits => ({
  id: b.id, min: parseFloat(b.min_ppm), max: parseFloat(b.max_ppm),
});

function hasBandAbove(band: BandLimits, all: BandLimits[]): boolean {
  return all.some((other) => other.id !== band.id && other.min === band.max);
}

/**
 * The max a band shows: one step below the min of the band above it, or the
 * stored max for the band at the top of the scale.
 *
 * @param band - The band's limits
 * @param all - Every band in the set
 * @returns The max to display
 * @example shownMax({ id: 5, min: 0, max: 50 }, bands) // 49.99 when D starts at 50
 */
export function shownMax(band: BandLimits, all: BandLimits[]): number {
  return hasBandAbove(band, all) ? toStep(band.max - BAND_STEP) : band.max;
}

/**
 * The max to store for one typed into the editor — the inverse of `shownMax`.
 *
 * A saved band is stepped up when it had a band above it. A new band (`null`)
 * is stepped up when the typed max sits one step below an existing band's min.
 *
 * @param typed - The max as typed
 * @param band - The saved band being edited, or null when adding one
 * @param all - Every band in the set
 * @returns The max to send to the API
 */
export function storedMax(typed: number, band: BandLimits | null, all: BandLimits[]): number {
  const stepUp = toStep(typed + BAND_STEP);
  const above = band ? hasBandAbove(band, all) : all.some((other) => other.min === stepUp);
  return above ? stepUp : typed;
}

/**
 * The stored max for a typed value, as the string the API takes; text that is
 * not a number goes through untouched so the API can say what is wrong.
 *
 * @param typed - The max field's text
 * @param band - The saved band being edited, or null when adding one
 * @param all - Every band in the set
 * @returns The max to send
 */
export function storedMaxText(typed: string, band: BandLimits | null, all: BandLimits[]): string {
  const value = parseFloat(typed);
  return Number.isFinite(value) ? storedMax(value, band, all).toFixed(2) : typed;
}

/**
 * A band's range as listed, e.g. "50.00 - 69.99".
 *
 * @param band - The band's limits
 * @param all - Every band in the set
 * @param separator - Between the two limits
 * @returns The range text, without a unit
 */
export function bandRangeText(band: BandLimits, all: BandLimits[], separator = ' - '): string {
  return `${band.min.toFixed(2)}${separator}${shownMax(band, all).toFixed(2)}`;
}

/** OTIF bands best first: highest minimum at the top. */
export const byOtifBest = (a: OTIFGradeThreshold, b: OTIFGradeThreshold): number =>
  parseFloat(b.min_otif_percent) - parseFloat(a.min_otif_percent);

/** PPM bands best first: lower PPM is better, so lowest minimum at the top. */
export const byPpmBest = (a: PPMGradeThreshold, b: PPMGradeThreshold): number =>
  parseFloat(a.min_ppm) - parseFloat(b.min_ppm);
